// src/stores/projectStore.js
import { create } from 'zustand';
import * as projectService from '../services/projectService';
import useUIStore from './uiStore';

const useProjectStore = create((set, get) => ({
  // State
  projects: [],
  totalProjects: 0,
  currentProject: null,
  isLoading: false,
  criticalError: null,        // Only for project loading failures
  operationError: null,       // For non-critical operations (team, milestones, etc.)
  meta: {},
  
  // Fetch projects with pagination and filters
  fetchProjects: async (params = {}) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await projectService.getProjects(params);
      set({
        projects: response.data.projects,
        totalProjects: response.meta.totalCount,
        meta:response.meta,
        isLoading: false
      });
      return response;
    } catch (error) {
      set({
        isLoading: false,
        criticalError: error.message || 'Failed to fetch projects'
      });
     console.log('error', error);
    }
  },
  
  // Fetch projects by client
  fetchClientProjects: async (clientId, params = {}) => {
    set({ isLoading: true, criticalError: null });
    try {
      const response = await projectService.getClientProjects(clientId, params);
      set({
        projects: response.data.projects,
        totalProjects: response.meta.totalCount,
        isLoading: false
      });
      return response;
    } catch (error) {
      set({
        isLoading: false,
        criticalError: error.message || 'Failed to fetch client projects'
      });
     console.log('error', error);
    }
  },
  
  // Fetch a single project by ID
  fetchProject: async (projectId) => {
    set({ isLoading: true, criticalError: null, currentProject: null });
    try {
      const response = await projectService.getProject(projectId);
      set({
        currentProject: response.data.project,
        isLoading: false
      });
      return response.data.project;
    } catch (error) {
      set({
        isLoading: false,
        criticalError: error.message || 'Failed to fetch project details'
      });
     console.log('error', error);
    }
  },
  
  // Create a new project
  createProject: async (projectData) => {
    set({ isLoading: true });
    try {
      const response = await projectService.createProject(projectData);
      set(state => ({
        projects: [response.data.project, ...state.projects],
        totalProjects: state.totalProjects + 1,
        isLoading: false
      }));
      
      // Success notification
      useUIStore.getState().addNotification({
        message: 'Project created successfully',
        type: 'success'
      });
      
      return response.data.project;
    } catch (error) {
      set({ isLoading: false });
      
      // Error notification - doesn't break layout
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to create project',
        type: 'error'
      });
      
      throw error; // Re-throw for component handling if needed
    }
  },
  
  // Update an existing project
  updateProject: async (projectId, projectData) => {
    set({ isLoading: true });
    try {
      const response = await projectService.updateProject(projectId, projectData);
      set(state => ({
        projects: state.projects.map(project => 
          project._id === projectId ? response.data.project : project
        ),
        currentProject: response.data.project,
        isLoading: false
      }));
      
      // Success notification
      useUIStore.getState().addNotification({
        message: 'Project updated successfully',
        type: 'success'
      });
      
      return response.data.project;
    } catch (error) {
      set({ isLoading: false });
      
      // Error notification - doesn't break layout
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to update project',
        type: 'error'
      });
      
      throw error;
    }
  },
  
  // Delete a project
  deleteProject: async (projectId) => {
    set({ isLoading: true });
    try {
      await projectService.deleteProject(projectId);
      set(state => ({
        projects: state.projects.filter(project => project._id !== projectId),
        totalProjects: state.totalProjects - 1,
        currentProject: state.currentProject?._id === projectId ? null : state.currentProject,
        isLoading: false
      }));
      
      useUIStore.getState().addNotification({
        message: 'Project deleted successfully',
        type: 'success'
      });
      
      return true;
    } catch (error) {
      set({ isLoading: false });
      
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to delete project',
        type: 'error'
      });
      
      throw error;
    }
  },
  
  // Update project status
  updateProjectStatus: async (projectId, status) => {
    set({ isLoading: true });
    try {
      const response = await projectService.updateProjectStatus(projectId, status);
      set(state => ({
        projects: state.projects.map(project => 
          project._id === projectId ? response.data.project : project
        ),
        currentProject: state.currentProject?._id === projectId 
          ? response.data.project 
          : state.currentProject,
        isLoading: false
      }));
      
      useUIStore.getState().addNotification({
        message: 'Project status updated successfully',
        type: 'success'
      });
      
      return response.data.project;
    } catch (error) {
      set({ isLoading: false });
      
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to update project status',
        type: 'error'
      });
      
      throw error;
    }
  },
  
  // Add a milestone to a project
  addProjectMilestone: async (projectId, milestoneData) => {
    try {
      const response = await projectService.addProjectMilestone(projectId, milestoneData);
      
      // Update the current project if it's the same as the one being modified
      if (get().currentProject && get().currentProject._id === projectId) {
        const updatedProject = {
          ...get().currentProject,
          milestones: [...get().currentProject.milestones, response.data.milestone]
        };
        
        set({ currentProject: updatedProject });
      }
      
      useUIStore.getState().addNotification({
        message: 'Milestone added successfully',
        type: 'success'
      });
      
      return response.data.milestone;
    } catch (error) {
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to add milestone',
        type: 'error'
      });
      throw error;
    }
  },
  
  // Update a milestone
  updateProjectMilestone: async (projectId, milestoneId, milestoneData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectService.updateProjectMilestone(
        projectId, 
        milestoneId, 
        milestoneData
      );
      
      // Update the current project if it's the same as the one being modified
      if (get().currentProject && get().currentProject._id === projectId) {
        const updatedMilestones = get().currentProject.milestones.map(milestone => 
          milestone._id === milestoneId ? response.data.milestone : milestone
        );
        
        const updatedProject = {
          ...get().currentProject,
          milestones: updatedMilestones
        };
        
        set({
          currentProject: updatedProject,
          isLoading: false
        });
      } else {
        set({ isLoading: false });
      }
      
      return response.data.milestone;
    } catch (error) {
      set({ isLoading: false });
      
      useUIStore.getState().addNotification({
        message: error.message || 'Failed to update milestone',
        type: 'error'
      });
      
      throw error;
    }
  },
  
  // Add a team member to a project
  addProjectTeamMember: async (projectId, teamMemberData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectService.addProjectTeamMember(projectId, teamMemberData);
      
      // Update the current project if it's the same as the one being modified
      if (get().currentProject && get().currentProject._id === projectId) {
        const updatedProject = {
          ...get().currentProject,
          team: [...get().currentProject.team, response.data.teamMember]
        };
        
        set({
          currentProject: updatedProject,
          isLoading: false
        });
      } else {
        set({ isLoading: false });
      }
      
      return response.data.teamMember;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to add team member'
      });
     console.log('error', error);
    }
  },
  
  // Update a team member
  updateProjectTeamMember: async (projectId, memberId, teamMemberData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectService.updateProjectTeamMember(
        projectId, 
        memberId, 
        teamMemberData
      );
      
      // Update the current project if it's the same as the one being modified
      if (get().currentProject && get().currentProject._id === projectId) {
        const updatedTeam = get().currentProject.team.map(member => 
          member._id === memberId ? response.data.teamMember : member
        );
        
        const updatedProject = {
          ...get().currentProject,
          team: updatedTeam
        };
        
        set({
          currentProject: updatedProject,
          isLoading: false
        });
      } else {
        set({ isLoading: false });
      }
      
      return response.data.teamMember;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to update team member'
      });
     console.log('error', error);
    }
  },
  
  // Remove a team member from a project
  removeProjectTeamMember: async (projectId, memberId) => {
    set({ isLoading: true, error: null });
    try {
      await projectService.removeProjectTeamMember(projectId, memberId);
      
      // Update the current project if it's the same as the one being modified
      if (get().currentProject && get().currentProject._id === projectId) {
        const updatedTeam = get().currentProject.team.filter(
          member => member._id !== memberId
        );
        
        const updatedProject = {
          ...get().currentProject,
          team: updatedTeam
        };
        
        set({
          currentProject: updatedProject,
          isLoading: false
        });
      } else {
        set({ isLoading: false });
      }
      
      return true;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to remove team member'
      });
     console.log('error', error);
    }
  },
  
  // Add a budget transaction
  addBudgetTransaction: async (projectId, transactionData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectService.addBudgetTransaction(projectId, transactionData);
      
      // Update the current project
      set({
        currentProject: response.data.project,
        isLoading: false
      });
      
      // Also update in the projects list if it exists there
      set(state => ({
        projects: state.projects.map(project => 
          project._id === projectId ? response.data.project : project
        )
      }));
      
      return response.data.project;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to add budget transaction'
      });
     console.log('error', error);
    }
  },
  
  // Upload a project document
  uploadProjectDocument: async (projectId, file, documentData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectService.uploadProjectDocument(projectId, file, documentData);
      
      // Update the current project if it's the same as the one being modified
      if (get().currentProject && get().currentProject._id === projectId) {
        const updatedProject = {
          ...get().currentProject,
          documents: [...get().currentProject.documents, response.data.document]
        };
        
        set({
          currentProject: updatedProject,
          isLoading: false
        });
      } else {
        set({ isLoading: false });
      }
      
      return response.data.document;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to upload document'
      });
     console.log('error', error);
    }
  },

  // Delete a project document
  deleteProjectDocument: async (projectId, documentId) => {
    set({ isLoading: true, operationError: null });
    try {
      await projectService.deleteProjectDocument(projectId, documentId);
      
      // Update the current project if it's the same as the one being modified
      if (get().currentProject && get().currentProject._id === projectId) {
        const updatedProject = {
          ...get().currentProject,
          documents: get().currentProject.documents.filter(doc => doc._id !== documentId)
        };
        
        set({
          currentProject: updatedProject,
          isLoading: false
        });
      } else {
        set({ isLoading: false });
      }
      
      useUIStore.getState().addNotification({
        type: 'success',
        message: 'Document deleted successfully'
      });
      
      return true;
    } catch (error) {
      set({
        isLoading: false,
        operationError: error.message || 'Failed to delete document'
      });
      
      useUIStore.getState().addNotification({
        type: 'error',
        message: error.message || 'Failed to delete document'
      });
      
      console.log('error', error);
      throw error;
    }
  },

  // Update a project document
  updateProjectDocument: async (projectId, documentId, documentData) => {
    set({ isLoading: true, operationError: null });
    try {
      const response = await projectService.updateProjectDocument(projectId, documentId, documentData);
      
      // Update the current project if it's the same as the one being modified
      if (get().currentProject && get().currentProject._id === projectId) {
        const updatedProject = {
          ...get().currentProject,
          documents: get().currentProject.documents.map(doc => 
            doc._id === documentId ? response.data.document : doc
          )
        };
        
        set({
          currentProject: updatedProject,
          isLoading: false
        });
      } else {
        set({ isLoading: false });
      }
      
      useUIStore.getState().addNotification({
        type: 'success',
        message: 'Document updated successfully'
      });
      
      return response.data.document;
    } catch (error) {
      set({
        isLoading: false,
        operationError: error.message || 'Failed to update document'
      });
      
      useUIStore.getState().addNotification({
        type: 'error',
        message: error.message || 'Failed to update document'
      });
      
      console.log('error', error);
      throw error;
    }
  },


// Delete budget transaction
// deleteBudgetTransaction: async (projectId, transactionId) => {
//   set({ isLoading: true, error: null });
//   try {
//     await projectService.deleteBudgetTransaction(projectId, transactionId);
    
//     // Update project in state
//     set(state => {
//       const projects = [...state.projects];
//       const projectIndex = projects.findIndex(p => p._id === projectId);
      
//       if (projectIndex !== -1) {
//         const project = { ...projects[projectIndex] };
//         project.transactions = (project.transactions || [])
//           .filter(t => t._id !== transactionId);
//         projects[projectIndex] = project;
//       }
      
//       return { projects, isLoading: false };
//     });
//   } catch (error) {
//     set({
//       isLoading: false,
//       error: error.message || 'Failed to delete transaction'
//     });
//     throw error;
//   }
// },
  // Clear current project
  clearCurrentProject: () => {
    set({ currentProject: null });
  },
  
  // Clear critical error
  clearError: () => {
    set({ criticalError: null });
  },
  
  // Clear operation error
  clearOperationError: () => {
    set({ operationError: null });
  }
}));

export default useProjectStore;